import {
    NBPContextProvider,
    NBPPopup,
    ProfileType,
    TcpaConsentParams,
    TCPAConsentType,
    TriggerMFAParams,
    VerifyOtpParams
} from '@fmc-digital/ui';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    selectLoanNumberSelected,
    selectNBP,
    selectRefactorNBPCard,
    selectSelectedLoanNumber,
    selectUserInfo
} from '@store/selectors/widgets.selectors';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getFullAppFeatureFlagsV2, getNBPFeatureFlag } from '@store/reducers/config.reducer';
import { get, groupBy, isEmpty } from 'lodash';
import NBPTermsTemplate from 'mocks/nbpTemplateTerms';
import { logApiEvent, logUserEvent } from '@analytics/amplifyLogging';
import { auth, config } from 'config';
import { activityEventUpdate, EventType } from 'fmc-digital-common';
import { RootState } from '@store';
import { allowedVWOActionsMapping, UnTargetedOffer } from '@constants';
import { dispatch } from '@store';
import { clearLoanNDStatus, updateNbpCardsRefactor } from '@store/actions/widgets.actions';
import { fetchPaperlessAsync } from '@store/actions/paperless.action';
import { useHomeContext } from '@components_v2/context/MyHomeContext';
import { NaturalDisasterConstants } from 'shared-ui-components';
import { enqueueSnackbar } from 'notistack';
import { useRequestForbearanceMutation } from '@hooks/useRequestForbearanceMutation';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackBarProps } from '@hooks/useSnackbarProps';
import { useUpdateTCPAConsent } from '@hooks/useUpdateTCPAConsent';
import { useTriggerMfa } from '@hooks/tcpa/useTriggerMfa';
import { useValidateOtp } from '@hooks/tcpa/useVerifyOtp';
import { useVWOContext } from 'context/VWOContext';
import { useProfileMutation } from '@hooks/useProfileMutation';
import { updateNbpCardRefactorLoc } from '@utils/filterWidgets';

const NBPoffer: React.FC = () => {
    const isMount = useRef(false);
    const { RecordVWOEvent } = useVWOContext();
    const token = auth.getOkta().getAccessToken();
    const selectedLoanNumber = useSelector(selectSelectedLoanNumber);
    const allNBPcards = useSelector(selectNBP);
    const pendingNBPcards = useSelector((state: RootState) => selectRefactorNBPCard(state));
    const navigate = useNavigate();
    const isNBP = useSelector(getNBPFeatureFlag);
    const userInfoSelector = useSelector(selectUserInfo);
    const loanNumber = useSelector(selectLoanNumberSelected);
    const card = get(pendingNBPcards, '0', undefined);
    const [deepLinkType, setDeepLinkType] = useState(null);
    const context = useHomeContext();
    const queryClient = useQueryClient();
    const snackProps = useSnackBarProps({ severity: 'error' });
    const [dialogContentKey, setDialogContentKey] = useState('');

    const updateTcpaConsentMutation = useUpdateTCPAConsent();
    const updateProfileMutation = useProfileMutation();
    const [mfaId, setMfaId] = useState('');
    const [mfaValidationStatus, setMfaValidationStatus] = useState<string>('');
    const [mfaErrorMsg, setMfaErrorMsg] = useState<string>('');
    const [mfaErrorCode, setMfaErrorCode] = useState<string>('');
    const [addressError, setAddressError] = useState(0);

    const [isAddressError, setIsAddressError] = useState(false);

    const flags = useSelector(getFullAppFeatureFlagsV2);
    const triggerMfaMutation = useTriggerMfa();
    const validateMfaMutation = useValidateOtp();
    const { mobile, email, firstName } = userInfoSelector;

    const onDeepLink = async (data: any) => {
        if (data.startsWith('#')) {
            const contentId = data.slice(1);
            handleNBPPopupClose();
            const scrollToContainer = document.getElementById(contentId);
            if (scrollToContainer) {
                scrollToContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }
        switch (data) {
            case 'paperlessEnroll':
                {
                    handleNBPPopupClose();
                    dispatch(fetchPaperlessAsync.request(selectedLoanNumber.identifier));
                }
                break;
            case 'od':
            case 'offer':
                setDeepLinkType(data);
                break;
            case 'welcome':
            case 'paperlessCancel':
            case 'downloadApp':
                {
                    handleNBPPopupClose();
                }
                break;
            case 'nda_yes':
                {
                    handleNBPPopupClose();
                    navigate('natural-disaster', { replace: true });
                }
                break;
            case 'nda_no':
                {
                    handleNotImpacted(loanNumber);
                }
                break;
            case 'ach':
                handleNBPPopupClose();
                context
                    .accountDetailSSO(`${config.env('myLoanBaseUrl')}/LoanService/SSOPost?target_menu=recurringPayments`)
                    .catch(console.log);
                break;
            case 'tcpa':
                setDialogContentKey(data);
                break;
            case 'Address':
                setDialogContentKey('');
                break;
            default: {
                const offerD = {
                    ...template?.metadata?.loanData,
                    offerType: card?.product,
                    loanNumber: card?.loanNumber,
                    leadOriginationSource: 'Popup'
                };

                if (data === 'fullapp') {
                    if (template?.metadata?.loanData && get(flags, [template?.metadata?.loanData.product_Campaign], false)) {
                        navigate(`${data}/${selectedLoanNumber.identifier}`, {
                            state: { offer: template?.metadata?.loanData, offerType: data, loanNumber: selectedLoanNumber.identifier },
                            preventScrollReset: false
                        });
                    } else {
                        navigate(`${template?.metadata?.loanData.product_Campaign}/${selectedLoanNumber.identifier}`, {
                            state: offerD
                        });
                    }
                } /// offer deep link
                else {
                    navigate(`${data}/${selectedLoanNumber.identifier}`, { state: offerD });
                }

                handleNBPPopupClose();
            }
        }
    };
    const apiEvents = useCallback((event: EventType) => {
        logApiEvent({
            apiName: event.apiName,
            action: event.action,
            correlationId: event.correlationId,
            timestamp: event.timestamp,
            loanNumber: event.loanNumber,
            description: event.description,
            type: 'Natural Disaster NBP'
        });
    }, []);

    const userEvents = useCallback((event: EventType) => {
        logUserEvent({
            type: 'Natural Disaster NBP',
            action: event.action,
            loanNumber: selectedLoanNumber?.identifier,
            description: event.description
        });
    }, []);

    const requestForbearance = useRequestForbearanceMutation({
        forbearanceApiParams: {
            baseAWSUrl: config.env('natDisasterAWSUrl') + `/${selectedLoanNumber?.identifier}/natural-disaster/`,
            xApiKey: config.env('xApiKey')
        },
        onComplete: async () => {
            await queryClient.invalidateQueries(['widgets']);
        },
        logApiEvent: apiEvents,
        logUserEvent: userEvents,
        type: 'DISASTER',
        accessToken: auth.getOkta().getAccessToken(),
        currentLoan: selectedLoanNumber
    });

    const handleNotImpacted = (loanNumber: string) => {
        dispatch(clearLoanNDStatus(loanNumber));
        const request = {
            borrowerImpacted: 'N',
            determinationMethod: 'BORROWER NOTIFICATION',
            label: 'No',
            propertyImpactDetermination: 'N',
            workplaceImpactDetermination: 'N'
        };
        requestForbearance.mutate(request, {
            onSuccess: () => {
                handleNBPPopupClose();
                navigate('disaster-assistance', { replace: true });
            },
            onError: () => {
                enqueueSnackbar(NaturalDisasterConstants.FAILURE_FORBEARANCE_MESSAGE, snackProps);
                handleNBPPopupClose();
                navigate('disaster-assistance', { replace: true });
            }
        });
    };

    const logUserEventHandler = useCallback(
        (action: string) => {
            if (!isEmpty(card)) {
                const data = {
                    action: action,
                    description: `${card?.product}`,
                    loanNumber: card?.loanNumber,
                    loanType: card.portCode,
                    product: card.product,
                    type: 'NextBestPopups'
                };
                logUserEvent(data);
                const vwoEvent = allowedVWOActionsMapping[action || null];
                vwoEvent ? RecordVWOEvent(vwoEvent, { product: card.product }) : null;
            }
        },
        [card]
    );

    const triggerDisplayEvent = useCallback(
        (template: any) => {
            if (template?.metadata) {
                const {
                    metadata: { userEvent }
                } = template;
                if (userEvent?.displayEvent) {
                    logUserEventHandler(userEvent.displayEvent.action);
                } else {
                    logUserEventHandler('OfferDisplayed');
                }
            }
        },
        [logUserEventHandler]
    );

    const template = useMemo(() => {
        if (deepLinkType === 'od') {
            let productCode: string = card?.product;
            if (UnTargetedOffer.includes(productCode)) {
                productCode = 'untarget';
            }
            const bodyContent = [
                {
                    type: 'headerText',
                    content: 'Terms and Conditions'
                },
                {
                    type: 'paragraph',
                    content: `${productCode.toLowerCase()}Terms`
                }
            ];
            const updatedTerms = { ...NBPTermsTemplate, body: bodyContent };
            return updatedTerms;
        }
        if (card?.product === 'DISASTER') {
            logUserEventHandler('VerifyNaturalDisasterFlag');
        }
        return card?.template;
    }, [deepLinkType, card]);

    const showNextOffer = async () => {
        dispatch(updateNbpCardsRefactor(updateNbpCardRefactorLoc(allNBPcards, card?.loanNumber, card?.product)));
        isMount.current = false;
    };

    const handleNBPPopupClose = () => {
        setDialogContentKey('');
        const url = config.env('activityUpdateUrl');
        if (deepLinkType === 'od') setDeepLinkType('offer');
        else if (card) {
            activityEventUpdate(
                [
                    {
                        loanNumber: card?.loanNumber,
                        activityType: card?.product,
                        activityResult: 'SHOWN'
                    }
                ],
                url
            );
            showNextOffer();
        }
    };

    useEffect(() => {
        if (
            (card?.template?.metadata?.popupTemplate === 'TCPA' || card?.template?.metadata?.popupTemplate === 'OB-TCPA') &&
            mobile?.length !== 10
        ) {
            setDialogContentKey('tcpa');
        }
    }, [mobile, card]);

    const handleTCPAConsent = (params: TcpaConsentParams) => {
        let mobileNumber = userInfoSelector?.mobile;
        if (params.mobileNumber && params.consentType === TCPAConsentType.Agree) {
            mobileNumber = params.mobileNumber;
        }
        setMfaValidationStatus('exceededMaxCount');
        updateTcpaConsentMutation.mutate({
            tcpa: params.consentType,
            mobile: mobileNumber,
            tcpaRequest: {
                authToken: token,
                xApiKey: config.env('xApiKey'),
                loanNumber: loanNumber,
                url: `${config.env('awsUrl')}/api/v1/`
            },
            logApiEventHandler: logApiEvent
        });
    };

    const handleProfileUpdate = (profile: ProfileType) => {
        setIsAddressError(false);
        updateProfileMutation.mutate(
            {
                profile: profile,
                accountNumber: selectedLoanNumber?.identifier,
                username: email,
                profileRequest: {
                    authToken: token,
                    xApiKey: config.env('xApiKey'),
                    loanNumber: loanNumber,
                    url: `${config.env('awsUrl')}/api/v1/`
                }
            },
            {
                onSuccess: () => {
                    setIsAddressError(false);
                    setDialogContentKey('addressSuccess');
                },
                onError: (error: any) => {
                    setAddressError(addressError + 1);
                    setIsAddressError(true);
                    console.log('profile update error');
                    console.log(error);
                }
            }
        );
    };

    const handleMFATrigger = useCallback(
        (params: TriggerMFAParams) => {
            try {
                triggerMfaMutation.mutateAsync(
                    {
                        mfaId: params.isResend ? mfaId : '',
                        mobileNumber: params.mobileNumber,
                        resend: params.isResend,
                        triggerMfaUrl: config.env('triggerMfaUrl'),
                        authToken: token,
                        xApiKey: config.env('xApiKey')
                    },
                    {
                        onSuccess: data => {
                            if (data.success) {
                                setMfaId(data.mfaId);
                            }
                        },
                        onError: (error: any) => {
                            console.log('trigger mfa error:' + error);
                        }
                    }
                );
            } catch (e) {
                console.log('error in catch', e);
            } finally {
                console.log('finally');
            }
        },
        [mfaId]
    );
    const getErrorMessage = (error: any) => {
        let errorMessage = '';
        if (error?.response?.data?.errorDetails) errorMessage = error?.response?.data?.errorDetails?.message;
        else errorMessage = error?.message;

        return errorMessage;
    };

    const handleMFAVerify = useCallback(
        (params: VerifyOtpParams) => {
            if (mfaId?.length) {
                validateMfaMutation.mutate(
                    {
                        mfaId: mfaId,
                        mfaCode: params.otp,
                        phoneNumber: params.mobileNumber,
                        validateMfaUrl: config.env('validateMfaUrl'),
                        authToken: token,
                        xApiKey: config.env('xApiKey')
                    },
                    {
                        onSuccess: (data: any) => {
                            const isSuccess = get(data, ['data', 'success'], false);
                            if (isSuccess) {
                                setMfaValidationStatus('success');
                            }
                        },
                        onError: (error: any) => {
                            const errorCode = get(error, ['response', 'data', 'errorDetails', 'code'], '');
                            const errorMessage = getErrorMessage(error);
                            setMfaValidationStatus(errorCode === 'CUST_S1200010' ? 'exceededMaxCount' : 'error');
                            setMfaErrorMsg(errorMessage);
                        }
                    }
                );
            }
        },
        [mfaId]
    );

    useEffect(() => {
        if (!isMount.current) {
            isMount.current = true;
            triggerDisplayEvent(template);
        }
    }, [template, triggerDisplayEvent]);

    return (
        !isEmpty(template) && (
            <NBPContextProvider
                template={template}
                onDeepLink={onDeepLink}
                isNBPOpen={isNBP && !isEmpty(pendingNBPcards) && !isEmpty(card)}
                handleNBPClose={handleNBPPopupClose}
                logUserEventHandler={logUserEventHandler}
                mobileNumber={mobile}
                dialogProperties={{
                    contentKey: dialogContentKey,
                    mfaErrorMsg: mfaErrorMsg,
                    mfaErrorCode: mfaErrorCode,
                    mfaValidationStatus: mfaValidationStatus,
                    isMailingAddressError: isAddressError,
                    addressError: addressError,
                    updateContentKey: setDialogContentKey,
                    handleTCPAConsent: handleTCPAConsent,
                    handleMFATrigger: handleMFATrigger,
                    handleMFAVerify: handleMFAVerify,
                    handleAddressSave: handleProfileUpdate
                }}
                borrowerFirstName={firstName}
            >
                <NBPPopup />
            </NBPContextProvider>
        )
    );
};

export default React.memo(NBPoffer);
